const OSRM_BASE = process.env.REACT_APP_OSRM_URL || 'https://router.project-osrm.org';

/**
 * Reduce GPS points for routing APIs while keeping start/end.
 */
export function thinRoutePoints(points, maxPoints = 80) {
  if (!points?.length || points.length <= maxPoints) return points || [];

  const step = Math.ceil(points.length / maxPoints);
  const thinned = points.filter((_, index) => index % step === 0);
  const last = points[points.length - 1];
  if (thinned[thinned.length - 1] !== last) thinned.push(last);
  return thinned;
}

function coordsToLatLngPairs(coordinates) {
  return coordinates.map(([lng, lat]) => [lat, lng]);
}

async function fetchOsrmGeometry(path) {
  const response = await fetch(path);
  if (!response.ok) return null;
  const data = await response.json();
  if (data.code !== 'Ok') return null;

  const geometry = data.matchings?.[0]?.geometry || data.routes?.[0]?.geometry;
  if (!geometry?.coordinates?.length) return null;

  return coordsToLatLngPairs(geometry.coordinates);
}

/**
 * Snap a GPS trace to likely roads using OSRM map matching, with fallbacks.
 * @param {{ lat: number, lng: number }[]} points
 * @returns {Promise<{ path: number[][], roadMatched: boolean }>}
 */
export async function matchGpsToRoadRoute(points) {
  const valid = (points || []).filter(
    (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)
  );
  if (valid.length < 2) {
    return {
      path: valid.map((p) => [p.lat, p.lng]),
      roadMatched: false,
    };
  }

  const thinned = thinRoutePoints(valid, 80);
  const matchCoords = thinned.map((p) => `${p.lng},${p.lat}`).join(';');

  try {
    const matched = await fetchOsrmGeometry(
      `${OSRM_BASE}/match/v1/driving/${matchCoords}?overview=full&geometries=geojson&steps=false&gaps=ignore`
    );
    if (matched?.length >= 2) {
      return { path: matched, roadMatched: true };
    }
  } catch (err) {
    console.warn('roadMatchRoute: map match failed', err);
  }

  try {
    const waypoints = thinRoutePoints(valid, 25);
    const routeCoords = waypoints.map((p) => `${p.lng},${p.lat}`).join(';');
    const routed = await fetchOsrmGeometry(
      `${OSRM_BASE}/route/v1/driving/${routeCoords}?overview=full&geometries=geojson&steps=false`
    );
    if (routed?.length >= 2) {
      return { path: routed, roadMatched: true };
    }
  } catch (err) {
    console.warn('roadMatchRoute: route fallback failed', err);
  }

  return {
    path: valid.map((p) => [p.lat, p.lng]),
    roadMatched: false,
  };
}
